import { config } from './config.js';
import { twinOrderViolations, describeTwinViolation } from './twinOrderRule.js';

// Phase 2B drag-to-reorder — the pure core, extracted so it is unit-testable without d3/DOM.
//
// Given the rendered layout `nodes`, the dragged person node, and the pixel-x it was dropped
// at, compute the NEW left-to-right order (Array<uid>) for the dragged node's GENERATION ROW
// only. The caller merges this into the cross-generation establishedOrder Map by index
// (rank = position in the returned array), leaving every other generation's ranks untouched.
//
// The drag is interpreted entirely in PIXEL space — what the user actually sees and drops
// against — NOT in virtualX. virtualX and pixel-x normally agree within an established row,
// but a customPositions pin on a peer (buildRenderModel overrides x, layout.js) can break that
// monotonicity; mixing the two spaces gave false no-ops and incoherent orders. So we order the
// OTHER peers by their rendered x, insert the dragged node at the pixel slot it was dropped in,
// and detect a no-op by comparing that slot to where the node STARTED (its pre-drag x).
//
// Contract (must stay aligned with captureEstablishedOrder in layout.js so a drag-derived order
// and a capture-derived order feed _applyLockedOrder identically):
//  - The order is the WHOLE generation row (grouped by Math.round(virtualY)), NOT a sibship —
//    _applyLockedOrder re-ranks the entire row, so a sibling-only order would violate the
//    contract. The user perceives a sibling swap; the data model reorders the row.
//  - Persons with uid == null are excluded (they cannot be ranked — see layout.js:485).
//
// `originalX` is the dragged node's x BEFORE the drag mutated it (the caller captures it at drag
// start; `dragged.x` by drag-end is the drop point, so it can't serve as the origin). Defaults
// to `dragged.x` for callers/tests where the node wasn't mutated.

// Partners by either side's spouseIds — the same test twinOrderRule's relationOf uses.
const partnered = (a, b) =>
  (a.spouseIds || []).some((id) => String(id) === String(b.pid)) ||
  (b.spouseIds || []).some((id) => String(id) === String(a.pid));

/**
 * The full result of interpreting a drag: `{ order, reason }`.
 *
 * `order` is Array<uid> (contiguous 0..n ranks when indexed), or null when there is nothing
 * to do — fewer than 2 rankable peers, the node ended in the slot it started in (a no-op the
 * caller must snap back without persisting, otherwise every micro-drag fires a save), or the
 * drop is one the chart could not draw honestly.
 *
 * `reason` is a sentence for the user, set ONLY for that last case. TGCLOUD-1789's second
 * acceptance criterion is that a genuine refusal must be visible rather than a silent
 * snap-back, and the caller cannot word it without knowing which rule objected.
 *
 * TGCLOUD-1789 — HOW A TWIN MOVES. Before this, a set always travelled as one block in its
 * on-screen order, and a co-twin could therefore never be repositioned relative to its own
 * co-twin: that gesture returned null and snapped back with no save and no undo step. That was
 * never a product decision — it was a workaround for the engine's atom rule, which reverted any
 * order that split a set, so the node snapped back AFTER a save had already fired
 * (PLAN-pedigree-multiple-births.md). The engine no longer demands row contiguity, so:
 *
 *   * a dragged person moves ALONE, twin or not;
 *   * if moving a co-twin alone would strand someone with drawn parents inside the set's
 *     span (TGCLOUD-1786 widened this from a sibling), the set travels together instead,
 *     which is the old behaviour and still the right answer there — but never so as to
 *     cut a set member off from a partner who stood beside them before the drag: that
 *     is a blocked marriage bar (1786 D8), and the couple's children would read as the
 *     co-twin's. Such a partner travels with the set, or the drop is refused;
 *   * anything the chart still could not draw honestly is refused with a reason.
 */
export function computeReorderOutcome(nodes, dragged, dropX, originalX) {
  const refuse = (reason = null) => ({ order: null, reason });
  if (!dragged || dragged.uid == null) return refuse();
  const startX = originalX ?? dragged.x ?? 0;

  const gen = Math.round(dragged.virtualY ?? 0);
  const peers = (nodes || []).filter(
    (n) =>
      n &&
      n.kind === config.NODE_TYPE.NODE &&
      n.uid != null &&
      Math.round(n.virtualY ?? 0) === gen
  );
  if (peers.length < 2) return refuse();

  // Who is on the chart, so the twin rule can tell a DRAWN parental line from a merely
  // recorded one (TGCLOUD-1786). Every person node counts, not only this row's: a parent
  // is on another row by definition, and one who is hidden is not in `nodes` at all.
  const byPid = new Map();
  for (const n of nodes || []) {
    if (!n || n.kind !== config.NODE_TYPE.NODE || n.pid == null) continue;
    if (!byPid.has(String(n.pid))) byPid.set(String(n.pid), n);
  }
  const personOf = (pid) => byPid.get(String(pid)) ?? null;

  const byScreen = (xOf) => (a, b) =>
    xOf(a) - xOf(b) ||
    (a.virtualX ?? 0) - (b.virtualX ?? 0) ||
    String(a.uid).localeCompare(String(b.uid));

  // A drop is judged by what it CHANGES. An interposer already on screen before the drag
  // — held inside a set by a freehand pin, say — is not this drop's doing, and counting it
  // would refuse every drag on the row with a reason about twins the user never touched.
  // It still counts once the gesture moves that interposer (or, for a split identical
  // pair, a member of that set), because the engine would then undo exactly that move.
  const keyOf = (v) => `${v.kind}|${v.setId}|${v.pid}`;
  const onScreen = [...peers].sort(
    byScreen((n) => (n.uid === dragged.uid ? startX : n.x ?? 0))
  );
  const already = new Set(twinOrderViolations(onScreen, personOf).map(keyOf));
  const brokenBy = (row, travelling) => {
    const movingPids = new Set(travelling.map((n) => String(n.pid)));
    const movingSets = new Set(
      travelling.map((n) => n.twinSubGroupId).filter(Boolean)
    );
    const touched = (v) =>
      v.kind === 'identical-split'
        ? movingSets.has(v.setId)
        : movingPids.has(String(v.pid));
    return twinOrderViolations(row, personOf).filter(
      (v) => !already.has(keyOf(v)) || touched(v)
    );
  };

  // The row that results from `travelling` landing at the drop point, or null when the
  // gesture changes nothing. Everyone else is ordered by what is on screen (rendered x),
  // tie-broken by virtualX then uid for determinism.
  const rowAfter = (travelling, { ordered = false } = {}) => {
    const block = ordered
      ? [...travelling]
      : [...travelling].sort((a, b) => (a.x ?? 0) - (b.x ?? 0));
    const moving = new Set(block.map((n) => n.uid));
    const others = peers
      .filter((n) => !moving.has(n.uid))
      .sort(byScreen((n) => n.x ?? 0));
    if (!others.length) return null; // nothing to reorder against

    // Both slots counted in the SAME (pixel) space: where it was dropped vs where it began.
    let insertIdx = 0;
    for (const n of others) if ((n.x ?? 0) < dropX) insertIdx++;
    let startIdx = 0;
    for (const n of others) if ((n.x ?? 0) < startX) startIdx++;
    if (insertIdx === startIdx) return null; // ends where it began

    return [
      ...others.slice(0, insertIdx),
      ...block,
      ...others.slice(insertIdx)
    ];
  };

  const alone = rowAfter([dragged]);
  if (!alone) return refuse();
  const aloneBreaks = brokenBy(alone, [dragged]);
  if (!aloneBreaks.length)
    return { order: alone.map((n) => n.uid), reason: null };

  // Moving the dragged co-twin on its own broke something. Carrying its set with it is the
  // other reading of the same gesture, and it is what the user means when the alternative
  // would leave a brother, sister, half-sibling or cousin standing inside the pair.
  //
  // But the fallback moves people the user did not drag, so it must not cut a set member
  // off from a partner who stood beside them before the drag (TGCLOUD-1786 review):
  // D6 made it fire for cousins, half-siblings and cross-married partners, and moving the
  // set alone then routinely left the partner on one side and the twin on the other with
  // a co-twin or a cousin between them — a blocked marriage bar, which D8 grades as a
  // defect. So the set is tried alone, then with those partners travelling beside their
  // spouses, and a drop that neither reading can draw is refused.
  if (dragged.twinSubGroupId) {
    const set = peers.filter(
      (n) => n.twinSubGroupId === dragged.twinSubGroupId
    );
    if (set.length > 1) {
      const inSet = new Set(set.map((n) => n.uid));
      // [member, partner, side] for every partner standing next to a set member on
      // screen before the drag; side -1 = on the member's left, +1 = on its right.
      const couples = [];
      onScreen.forEach((member, i) => {
        if (!inSet.has(member.uid)) return;
        for (const side of [-1, 1]) {
          const next = onScreen[i + side];
          if (next && !inSet.has(next.uid) && partnered(member, next))
            couples.push([member, next, side]);
        }
      });
      const keepsCouples = (row) =>
        couples.every(
          ([member, partner]) =>
            Math.abs(row.indexOf(member) - row.indexOf(partner)) === 1
        );
      const accept = (row, travelling) =>
        row && keepsCouples(row) && !brokenBy(row, travelling).length;

      const together = rowAfter(set);
      if (accept(together, set))
        return { order: together.map((n) => n.uid), reason: null };

      if (couples.length) {
        // The set in the order it travels (as rowAfter orders it), each partner on the
        // side of its spouse it stood on.
        const members = [...set].sort((a, b) => (a.x ?? 0) - (b.x ?? 0));
        const withPartners = members.flatMap((member) => {
          const beside = (side) =>
            couples
              .filter(([m, , s]) => m === member && s === side)
              .map(([, partner]) => partner);
          return [...beside(-1), member, ...beside(1)];
        });
        const coupled = rowAfter(withPartners, { ordered: true });
        if (accept(coupled, withPartners))
          return { order: coupled.map((n) => n.uid), reason: null };
      }
    }
  }

  return refuse(describeTwinViolation(aloneBreaks[0]));
}

/**
 * The order alone, for callers that cannot act on a reason. Kept so the many existing
 * call sites and tests read unchanged; Canvas uses `computeReorderOutcome` so it can tell
 * the user why a drop was refused.
 */
export function computeReorderForGeneration(nodes, dragged, dropX, originalX) {
  return computeReorderOutcome(nodes, dragged, dropX, originalX).order;
}

// Merge a single generation's new uid order into the full cross-generation order Map,
// re-ranking ONLY that generation (0..n) and preserving every other generation's ranks.
// Returns a new Map (never mutates `prev`).
export function mergeGenerationOrder(prev, uidsInOrder) {
  const next = new Map(prev || []);
  uidsInOrder.forEach((uid, i) => next.set(uid, i));
  return next;
}
